import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { formatApiError } from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[85vh] flex items-center justify-center px-4 py-12">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md z-10">
        <div className="surface-card p-7 sm:p-10 rise">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="register-title">
            {t("auth_register_title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1.5">{t("brand")}</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="register-form">
            <div>
              <label className="input-label">{t("auth_name")}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="input"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="input-label">{t("auth_email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                data-testid="register-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="input-label">{t("auth_password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="input"
                data-testid="register-password"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div
                className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3.5 py-3 text-sm"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="btn btn-primary btn-lg w-full disabled:opacity-60"
            >
              {loading ? t("common_loading") : t("auth_register_btn")}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </form>

          <p className="mt-7 text-sm text-center text-zinc-500">
            {t("auth_have_account")}{" "}
            <Link
              to="/login"
              data-testid="register-login-link"
              className="font-semibold text-zinc-900 hover:underline underline-offset-4"
            >
              {t("auth_login_link")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
